/**
 * Таблицы обозначений листов, у которых заливки сведены в машиночитаемый вид.
 *
 * Подсказка по наведению отвечает только там, где обозначение — сплошная
 * заливка: цвет пикселя можно сопоставить с образцом легенды. Листы, где
 * обозначения нарисованы линиями, штриховками и значками, сюда не входят и
 * входить не могут — по одному пикселю такое не опознать, а соврать на
 * градостроительном документе хуже, чем промолчать.
 *
 * Цвета сняты с картинок легенд в data/legends тем же способом, что и для
 * листа функциональных зон: в каждой строке берётся самый длинный одноцветный
 * отрезок. Названия прочитаны с тех же картинок и не придуманы.
 */

import { GENPLAN_ZONES_2024, type GenplanZone } from "./genplanZones2024";

/**
 * «Карта современного использования территории».
 *
 * Единственный лист про существующее положение — остальные показывают
 * проектные решения. Палитра почти совпадает с листом функциональных зон
 * (двадцать цветов из двадцати двух расходятся не более чем на 4 единицы при
 * допуске 28), но своих обозначений два: обобщающие «Жилые зоны» и
 * «Лесопарковая зона», которых в проектном зонировании нет. Поэтому таблица
 * отдельная, а не ссылка на зоны: подписи на этом листе свои.
 *
 * Наименьшее расстояние между образцами — 40,1 (режимные территории и
 * акватории). Это ровно тот же запас, что у листа зон, где голосование с
 * допуском 28 давно работает.
 */
export const GENPLAN_CURRENT_USE: GenplanZone[] = [
  { rgb: [254, 102, 78], title: "Жилые зоны", description: "" },
  { rgb: [255, 225, 49], title: "Зона застройки индивидуальными жилыми домами", description: "" },
  {
    rgb: [255, 169, 1],
    title: "Зона застройки малоэтажными жилыми домами",
    description: "До 4 этажей, включая мансардный.",
  },
  {
    rgb: [255, 84, 0],
    title: "Зона застройки среднеэтажными жилыми домами",
    description: "От 5 до 8 этажей, включая мансардный.",
  },
  {
    rgb: [255, 48, 49],
    title: "Зона застройки многоэтажными жилыми домами",
    description: "9 этажей и более.",
  },
  { rgb: [194, 0, 125], title: "Зона смешанной и общественно-деловой застройки", description: "" },
  { rgb: [255, 1, 196], title: "Общественно-деловые зоны", description: "" },
  { rgb: [202, 122, 245], title: "Зона специализированной общественной застройки", description: "" },
  { rgb: [164, 39, 167], title: "Многофункциональная общественно-деловая зона", description: "" },
  { rgb: [136, 91, 68], title: "Производственная зона", description: "" },
  { rgb: [189, 151, 132], title: "Коммунальная зона", description: "" },
  { rgb: [100, 99, 131], title: "Зона инженерной инфраструктуры", description: "" },
  { rgb: [2, 106, 145], title: "Зона транспортной инфраструктуры", description: "" },
  { rgb: [208, 224, 177], title: "Зона сельскохозяйственных угодий", description: "" },
  {
    rgb: [170, 255, 0],
    title: "Зона садоводческих, огороднических или дачных некоммерческих объединений граждан",
    description: "",
  },
  {
    rgb: [0, 255, 197],
    title: "Зона озеленённых территорий общего пользования",
    description: "Лесопарки, парки, сады, скверы, бульвары, городские леса.",
  },
  { rgb: [0, 176, 88], title: "Лесопарковая зона", description: "" },
  { rgb: [170, 205, 101], title: "Зоны специального назначения", description: "" },
  { rgb: [103, 182, 103], title: "Зона озеленённых территорий специального назначения", description: "" },
  { rgb: [207, 208, 254], title: "Зона режимных территорий", description: "" },
  { rgb: [138, 238, 184], title: "Иные зоны", description: "" },
  { rgb: [209, 248, 253], title: "Зона акваторий", description: "" },
];

/**
 * «Карта планируемого размещения объектов спорта и мест массового отдыха».
 *
 * Заливка на листе ровно одна, всё остальное — значки. Одна строка тоже имеет
 * смысл: цвет уникален для листа (до ближайшего образца соседних палитр 52
 * единицы), а озеленённые территории занимают заметную часть площади.
 */
export const GENPLAN_SPORT_RECREATION: GenplanZone[] = [
  { rgb: [87, 150, 142], title: "Озеленённые территории", description: "" },
];

/**
 * Какому листу какая таблица.
 *
 * Отсутствие листа в этом наборе — не недоработка, а утверждение: его
 * обозначения по цвету пикселя не опознаются, и подсказка по нему молчит.
 */
export const ZONE_TABLES_BY_SOURCE: Record<string, GenplanZone[]> = {
  // Обе редакции одного документа с одинаковой палитрой (сверено по легендам:
  // наибольшее расхождение 8,1 при допуске 28). Запись обоих означает, что при
  // возврате геопортального листа он заработает без правок.
  genplan_zones_072024: GENPLAN_ZONES_2024,
  genplan_zones_2024: GENPLAN_ZONES_2024,
  // Сводная карта закрашена тем же зонированием: все двадцать четыре образца
  // её легенды отстоят от палитры зон не более чем на 4 единицы, двенадцать
  // совпадают побайтово. Значки поверх заливки остаются неопознанными — это
  // известное ограничение, а не сбой.
  genplan_local_objects: GENPLAN_ZONES_2024,
  genplan_current_use: GENPLAN_CURRENT_USE,
  genplan_sport_recreation: GENPLAN_SPORT_RECREATION,
};
